import React, { useState } from 'react';
import { LogIn } from 'lucide-react';
import { useStorageData } from '../hooks/useStorageData';
import { useStats } from '../hooks/useStats';
import { useTheme } from '../hooks/useTheme';
import { useAuth } from '../hooks/useAuth';
import { ThemeToggle } from './ThemeToggle';
import { ExportButton } from './ExportButton';
import { TABS } from '../constants';
import { formatNumber, getCurrentWeek } from '../utils';

import { OverviewTab } from './tabs/OverviewTab';
import { ObjectivesTab } from './tabs/ObjectivesTab';
import { ROITab } from './tabs/ROITab';
import { ProjectsTab } from './tabs/ProjectsTab';
import { AirdropsReceivedTab } from './tabs/AirdropsReceivedTab';
import { CalendarTab } from './tabs/CalendarTab';
import { HistoryTab } from './tabs/HistoryTab';
import { AddEntryTab } from './tabs/AddEntryTab';
import { OnboardingTour } from './OnboardingTour';
import PromoPopup from './PromoPopup';

const TAB_LABELS = {
  overview: 'Overview',
  objectives: 'Targets',
  roi: 'ROI',
  projects: 'Airdrops',
  'airdrops-received': 'Received',
  calendar: 'Calendar',
  history: 'History',
  add: 'Add'
};

const CURRENT_WEEK = getCurrentWeek();

const DEMO_PROJECT_SETTINGS = {
  'Hyperliquid': {
    type: 'Trading',
    capital: 5000,
    tge: 'Q1 2025',
    startDate: '2024-06-15',
    priority: 'High',
    status: 'Active',
    notes: 'Focus on perpetual trading volume',
    active: true,
    metricType: 'points',
    weeklyTarget: 10000,
    finalTarget: 500000,
    roiScenarios: { bearish: 0.05, base: 0.15, bullish: 0.40 },
    isDemo: true
  },
  'Jupiter': {
    type: 'DeFi',
    capital: 2000,
    tge: 'Q4 2024',
    startDate: '2024-03-01',
    priority: 'Medium',
    status: 'Active',
    notes: 'Swap aggregator on Solana',
    active: true,
    metricType: 'points',
    weeklyTarget: 5000,
    finalTarget: 200000,
    roiScenarios: { bearish: 0.02, base: 0.08, bullish: 0.20 },
    isDemo: true
  },
  'Meteora': {
    type: 'DeFi',
    capital: 3000,
    tge: 'Q2 2025',
    startDate: '2024-09-01',
    priority: 'High',
    status: 'Active',
    notes: 'LP fees farming on Solana',
    active: true,
    metricType: 'fees',
    weeklyTarget: 150,
    finalTarget: 5000,
    roiScenarios: { bearish: 10, base: 25, bullish: 50 },
    isDemo: true
  }
};

const DEMO_WEEKLY_DATA = {
  weeks: {
    [CURRENT_WEEK]: [
      { id: 1, project: 'Hyperliquid', volume: 125000, cost: 45, points: 8500, fees: 0, customMetric: 0, metricType: 'points', date: new Date().toISOString() },
      { id: 2, project: 'Jupiter', volume: 50000, cost: 12, points: 3200, fees: 0, customMetric: 0, metricType: 'points', date: new Date().toISOString() },
      { id: 3, project: 'Meteora', volume: 25000, cost: 8, points: 0, fees: 120, customMetric: 0, metricType: 'fees', date: new Date().toISOString() }
    ],
    '2026-02-W3': [
      { id: 4, project: 'Hyperliquid', volume: 98000, cost: 38, points: 6800, fees: 0, customMetric: 0, metricType: 'points', date: '2026-02-17' },
      { id: 5, project: 'Jupiter', volume: 35000, cost: 8, points: 2100, fees: 0, customMetric: 0, metricType: 'points', date: '2026-02-16' },
      { id: 6, project: 'Meteora', volume: 18000, cost: 5, points: 0, fees: 95, customMetric: 0, metricType: 'fees', date: '2026-02-15' }
    ],
    '2026-02-W2': [
      { id: 7, project: 'Hyperliquid', volume: 110000, cost: 42, points: 7200, fees: 0, customMetric: 0, metricType: 'points', date: '2026-02-10' },
      { id: 8, project: 'Jupiter', volume: 42000, cost: 10, points: 2800, fees: 0, customMetric: 0, metricType: 'points', date: '2026-02-09' }
    ]
  }
};

const DEMO_RECEIVED_AIRDROPS = [
  {
    id: 1,
    project: 'Lighter',
    tokens: 54000,
    totalSold: 104000,
    dateReceived: '2025-12-15',
    sales: [
      { id: 101, percentage: 37, tokens: 20000, pricePerToken: 2.80, total: 56000, date: '2026-01-10' },
      { id: 102, percentage: 28, tokens: 15000, pricePerToken: 3.20, total: 48000, date: '2026-02-05' }
    ],
    isDemo: true
  },
  {
    id: 2,
    project: 'Jupiter',
    tokens: 12500,
    totalSold: 4250,
    dateReceived: '2024-01-31',
    sales: [
      { id: 201, percentage: 40, tokens: 5000, pricePerToken: 0.85, total: 4250, date: '2024-02-15' }
    ],
    isDemo: true
  }
];

const Toast = ({ message, onClose, isDark }) => {
  React.useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 px-4 w-full max-w-sm">
      <div className="bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3">
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
        <span className="font-semibold">{message}</span>
      </div>
    </div>
  );
};

const DemoBanner = ({ onSignUpClick, isDark }) => (
  <div className={`mb-4 p-3 rounded-lg border flex flex-col sm:flex-row items-center justify-between gap-3 ${isDark ? 'bg-blue-900/20 border-blue-700/30' : 'bg-blue-50 border-blue-200'}`}>
    <div className="flex items-center gap-2">
      <span className="text-2xl">👀</span>
      <span className={`text-sm ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
        <strong>Demo Mode:</strong> This is sample data. Create an account to track your own airdrops!
      </span>
    </div>
    <button
      onClick={onSignUpClick}
      className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors text-sm whitespace-nowrap"
    >
      Sign Up Free
    </button>
  </div>
);

const FarmDashboard = ({ isDemo = false, onLoginClick }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [toast, setToast] = useState(null);
  const [timeRange, setTimeRange] = useState('week');
  const { theme, isDark, colors } = useTheme();
  const { user, logout } = useAuth();

  const [demoProjectSettings, setDemoProjectSettings] = useState(DEMO_PROJECT_SETTINGS);
  const [demoWeeklyData, setDemoWeeklyData] = useState(DEMO_WEEKLY_DATA);
  const [demoReceivedAirdrops, setDemoReceivedAirdrops] = useState(DEMO_RECEIVED_AIRDROPS);
  const [demoAlerts, setDemoAlerts] = useState([
    { id: 1, message: 'Remember to check your daily points', active: true },
    { id: 2, message: 'Weekly target reminder', active: true }
  ]);
  const [demoCalendarEvents, setDemoCalendarEvents] = useState([
    { id: 1, title: 'Hyperliquid TGE', date: '2025-03-31', type: 'tge', project: 'Hyperliquid' },
    { id: 2, title: 'Meteora TGE', date: '2025-06-30', type: 'tge', project: 'Meteora' }
  ]);

  const showToast = (message) => setToast(message);

  const storageData = useStorageData();

  const loading = isDemo ? false : storageData.loading;
  const weeklyData = isDemo ? demoWeeklyData : storageData.weeklyData;
  const setWeeklyData = isDemo ? setDemoWeeklyData : storageData.setWeeklyData;
  const alerts = isDemo ? demoAlerts : storageData.alerts;
  const setAlerts = isDemo ? setDemoAlerts : storageData.setAlerts;
  const projectSettings = isDemo ? demoProjectSettings : storageData.projectSettings;
  const setProjectSettings = isDemo ? setDemoProjectSettings : storageData.setProjectSettings;
  const receivedAirdrops = isDemo ? demoReceivedAirdrops : storageData.receivedAirdrops;
  const setReceivedAirdrops = isDemo ? setDemoReceivedAirdrops : storageData.setReceivedAirdrops;
  const calendarEvents = isDemo ? demoCalendarEvents : storageData.calendarEvents;
  const setCalendarEvents = isDemo ? setDemoCalendarEvents : storageData.setCalendarEvents;
  const customAirdrops = isDemo ? [] : storageData.customAirdrops;
  const setCustomAirdrops = isDemo ? () => {} : storageData.setCustomAirdrops;

  const { stats, weeklyProgress, roiCalculation } = useStats(weeklyData, timeRange, projectSettings);

  const toggleAlert = (id) => {
    setAlerts(alerts.map(a => a.id === id ? { ...a, active: !a.active } : a));
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${colors.bgPrimary}`}>
        {toast && <Toast message={toast} onClose={() => setToast(null)} isDark={isDark} />}
        <div className={`text-xl ${colors.textPrimary}`}>Loading FarmDash...</div>
      </div>
    );
  }

  const tabProps = {
    weeklyData,
    setWeeklyData,
    projectSettings,
    setProjectSettings,
    receivedAirdrops,
    setReceivedAirdrops,
    calendarEvents,
    setCalendarEvents,
    customAirdrops,
    setCustomAirdrops,
    alerts,
    setAlerts,
    stats,
    weeklyProgress,
    roiCalculation,
    timeRange,
    setTimeRange,
    showToast,
    isDark,
    colors,
    isPro: true,
    openPricing: () => {},
    isDemo
  };

  return (
    <div className={`min-h-screen p-3 sm:p-6 transition-colors duration-300 ${colors.bgPrimary}`}>
      {!isDemo && <OnboardingTour userId={user?.uid} />}
      {!isDemo && <PromoPopup user={user} />}
      {toast && <Toast message={toast} onClose={() => setToast(null)} isDark={isDark} />}

      <div className="max-w-7xl mx-auto">
        {isDemo && <DemoBanner onSignUpClick={onLoginClick} isDark={isDark} />}

        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold mb-1 sm:mb-2">
                <span className="bg-gradient-to-r from-green-400 to-blue-500 bg-clip-text text-transparent">
                  FarmDash
                </span>
              </h1>
            </div>
            <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-4">
              {isDemo ? (
                <button
                  onClick={onLoginClick}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors text-sm"
                >
                  <LogIn size={16} />
                  <span>Sign In</span>
                </button>
              ) : (
                <ExportButton
                  weeklyData={weeklyData}
                  projectSettings={projectSettings}
                  receivedAirdrops={receivedAirdrops}
                  stats={stats}
                  roiCalculation={roiCalculation}
                  showToast={showToast}
                  isDark={isDark}
                  colors={colors}
                  isPro={true}
                  openPricing={() => {}}
                />
              )}
              <ThemeToggle />
              {!isDemo && (
                <>
                  <div className="text-right">
                    <div className={`text-xs sm:text-sm ${colors.textSecondary}`}>Estimated ROI (Base)</div>
                    <div className="text-xl sm:text-2xl font-bold text-green-400">
                      ${formatNumber(roiCalculation?.base || 0)}
                    </div>
                  </div>
                  <button
                    onClick={logout}
                    className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-gray-200 text-gray-600'}`}
                    title={`Logout (${user?.email || 'User'})`}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                      <polyline points="16 17 21 12 16 7"></polyline>
                      <line x1="21" y1="12" x2="9" y2="12"></line>
                    </svg>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="mb-4 sm:mb-6 -mx-3 sm:mx-0 px-3 sm:px-0">
          <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0 sm:flex-wrap scrollbar-hide">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-3 sm:px-4 py-2 rounded-lg font-medium text-xs sm:text-sm transition-all whitespace-nowrap flex-shrink-0 ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/50'
                    : `${colors.bgButton} ${colors.textSecondary} hover:${colors.bgButtonHover}`
                }`}
              >
                {TAB_LABELS[tab.id]}
              </button>
            ))}
          </div>
        </div>

        <div className="min-h-[400px] sm:min-h-[600px]">
          {activeTab === 'overview' && <OverviewTab {...tabProps} />}
          {activeTab === 'objectives' && <ObjectivesTab {...tabProps} />}
          {activeTab === 'roi' && <ROITab {...tabProps} />}
          {activeTab === 'projects' && <ProjectsTab {...tabProps} />}
          {activeTab === 'airdrops-received' && <AirdropsReceivedTab {...tabProps} />}
          {activeTab === 'calendar' && <CalendarTab {...tabProps} />}
          {activeTab === 'history' && <HistoryTab {...tabProps} />}
          {activeTab === 'add' && <AddEntryTab {...tabProps} />}
        </div>
      </div>
    </div>
  );
};

export default FarmDashboard;
